import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import * as ServerListActions from 'actions/ServerList';
import SelectHoi from 'components/SelectHoi';
import { getPrimaryEnvironmentValue, parseEnvironmentValues } from 'utils/multiEnvironment';
import { OneRow } from './components/rows';
import './components/rows/styles.scss';
import { getLabelPopoverConfigForList } from './labelPopoverRegistry';
import { createMultiValueLabelComponent, createSingleValueComponent } from './utils';

// Backend uses -1, 0, "" as "unassigned" values - treat as empty.
// Some lists use 0 as a real option id, so fields can opt out explicitly.
// Billboard environments use -1 for "My team".
const isBackendEmpty = (val, allowZeroValue = false, allowMinusOneValue = false) => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'object' && !Array.isArray(val)) {
        const id = val.value ?? val.id;
        const isZero = id === 0 || id === '0';
        const isMinusOne = id === -1 || id === '-1';
        return (!allowMinusOneValue && isMinusOne) || (!allowZeroValue && isZero) || id === '';
    }
    const isZero = val === 0 || val === '0';
    const isMinusOne = val === -1 || val === '-1';
    return (!allowMinusOneValue && isMinusOne) || (!allowZeroValue && isZero) || val === '';
};

// Default dependency filter based on idparent
const defaultDependenceFilter = (option, parentValue) => {
    const parentId =
        parentValue && typeof parentValue === 'object'
            ? (parentValue.value ?? parentValue.id)
            : parentValue;
    return (
        String(option.idparent) === String(parentId) ||
        option.idparent === '-1' ||
        option.idparent === '0' ||
        option.idparent === ''
    );
};

// Normalize value to primitive ID
const getValueId = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return value.value ?? value.id;
    return value;
};

const propTypes = {
    autoFocus: PropTypes.bool,
    allowZeroValue: PropTypes.bool,
    allowMinusOneValue: PropTypes.bool,
    beforeRenderValueList: PropTypes.func,
    className: PropTypes.string,
    customFilterOptions: PropTypes.func,
    customOption: PropTypes.func,
    dependenceFilter: PropTypes.func,
    description: PropTypes.string,
    disabled: PropTypes.bool,
    disableIfNoOptions: PropTypes.bool,
    emptyFilter: PropTypes.func,
    environmentField: PropTypes.object,
    error: PropTypes.any,
    fieldId: PropTypes.string,
    getExtraClassNameValue: PropTypes.func,
    groupFunction: PropTypes.func,
    mainGroupLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    otherGroupLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    hidden: PropTypes.bool,
    hideIfEmptyList: PropTypes.bool,
    hint: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    idFieldTable: PropTypes.string,
    isClearable: PropTypes.bool,
    labelPopover: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            parseDataForPopover: PropTypes.func,
            type: PropTypes.string,
            remotePopover: PropTypes.bool,
            getImage: PropTypes.func,
            image: PropTypes.node,
            renderSecondImage: PropTypes.func,
            doubleImageIds: PropTypes.object,
            trigger: PropTypes.array,
            placement: PropTypes.string,
            address: PropTypes.string,
            inactivityDays: PropTypes.node,
            noCache: PropTypes.bool,
        }),
    ]),
    isFuzzy: PropTypes.bool,
    isMulti: PropTypes.bool,
    isNewEntity: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    list: PropTypes.string,
    mandatory: PropTypes.bool,
    onChange: PropTypes.func,
    onHideChange: PropTypes.func,
    options: PropTypes.array,
    parentField: PropTypes.string,
    parentValue: PropTypes.any,
    placeholderReadOnly: PropTypes.string,
    readOnly: PropTypes.bool,
    readOnlyIfNoParent: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    usePlainValue: PropTypes.bool,
    value: PropTypes.any,
    workFlow: PropTypes.object,
};

const Select = ({
    allowZeroValue = false,
    allowMinusOneValue = false,
    beforeRenderValueList,
    className,
    customFilterOptions,
    customOption: customOptionProp,
    dependenceFilter,
    description,
    disabled: disabledProp,
    disableIfNoOptions,
    emptyFilter,
    environmentField,
    error,
    fieldId,
    getExtraClassNameValue,
    groupFunction,
    mainGroupLabel,
    otherGroupLabel,
    hidden,
    hideIfEmptyList,
    hint,
    idFieldTable,
    isClearable: isClearableProp = true,
    labelPopover,
    isFuzzy,
    isMulti,
    isNewEntity,
    label,
    list,
    loadOptions: loadOptionsProp,
    mandatory,
    onChange,
    onHideChange,
    options = [],
    parentField,
    parentValue,
    placeholderReadOnly,
    readOnly: readOnlyProp,
    readOnlyIfNoParent,
    shouldRenderField,
    usePlainValue: usePlainValueProp,
    value,
    workFlow,
    withoutFilter,
    noFilter,
    ...props
}) => {
    const dispatch = useDispatch();
    const [isEmptyForHide, setIsEmptyForHide] = useState(false);
    const [isEmptyForDisable, setIsEmptyForDisable] = useState(false);
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const [preloadedOptionsForClass, setPreloadedOptionsForClass] = useState([]);
    const [workflowReadOnly, setWorkflowReadOnly] = useState(false);

    const valueId = useMemo(() => getValueId(value), [value]);

    // Normalize value for SelectHoi
    const normalizedValue = useMemo(() => {
        if (isBackendEmpty(value, allowZeroValue, allowMinusOneValue)) return undefined;
        if (isMulti && Array.isArray(value)) {
            const filtered = value.filter(
                (v) => !isBackendEmpty(v, allowZeroValue, allowMinusOneValue),
            );
            return filtered.length > 0 ? filtered : undefined;
        }
        return value;
    }, [allowMinusOneValue, allowZeroValue, value, isMulti]);

    const shouldUsePreloadedOptions = useMemo(
        () => Boolean(list && getExtraClassNameValue && normalizedValue),
        [getExtraClassNameValue, list, normalizedValue],
    );

    // Store initial value for workflow - only set once on mount (like componentDidMount)
    // This prevents readonly from triggering when user selects a new value before saving
    const initialValueRef = useRef(null);
    if (initialValueRef.current === null && workFlow) {
        initialValueRef.current = valueId && !isNewEntity ? String(valueId) : '-1';
    }
    const workFlowFirstValue = initialValueRef.current || '-1';

    // Workflow filter - determines which options are available based on INITIAL value
    const workFlowFilter = useCallback(
        (option) => {
            if (!workFlow) return true;
            return (
                workFlow[workFlowFirstValue]?.relationsArray?.includes(option.value) ||
                (isNewEntity && valueId && option.value === String(valueId)) ||
                option.value === workFlowFirstValue
            );
        },
        [workFlowFirstValue, valueId, isNewEntity, workFlow],
    );

    // Load and filter options
    const loadOptions = useCallback(() => {
        return dispatch(ServerListActions.getList(list)).then((data) => {
            const originalOptions = data || [];
            let finalOptions = [...originalOptions];

            // Check hideIfEmptyList using ORIGINAL options with emptyFilter
            let isEmpty;
            if (emptyFilter) {
                const validOriginalOptions = originalOptions.filter(emptyFilter);
                isEmpty = !validOriginalOptions?.length;
            } else {
                isEmpty = !originalOptions?.length;
            }
            setIsEmptyForHide(isEmpty);

            // Notify parent to hide entire wrapper (including label)
            if (isEmpty && hideIfEmptyList && onHideChange) {
                onHideChange(true);
            }

            // 1. Apply dependency filter
            if (parentField && !readOnlyProp) {
                const filterFn =
                    dependenceFilter || ((opt) => defaultDependenceFilter(opt, parentValue));
                finalOptions = finalOptions.filter(filterFn);
            }

            // 2. Apply workflow filter and calculate readonly state
            if (workFlow) {
                finalOptions = finalOptions.filter(workFlowFilter);

                // Readonly when only 1 option available AND:
                // - New entity: always readonly (no other choice)
                // - Existing entity: only if value hasn't changed (user hasn't selected a new one)
                if (finalOptions.length === 1) {
                    const singleOptionValue = finalOptions[0].value;
                    const currentValueStr = valueId ? String(valueId) : null;

                    if (currentValueStr === singleOptionValue) {
                        // Existing entity: check if value hasn't changed
                        const valueNotChanged = currentValueStr === workFlowFirstValue;
                        if (valueNotChanged) {
                            const hasNoWorkflow = !workFlow[currentValueStr];
                            const cannotTransitionToSelf =
                                !workFlow[currentValueStr]?.relationsArray?.includes(
                                    currentValueStr,
                                );
                            setWorkflowReadOnly(hasNoWorkflow || cannotTransitionToSelf);
                        } else {
                            setWorkflowReadOnly(false);
                        }
                    } else {
                        setWorkflowReadOnly(false);
                    }
                } else {
                    setWorkflowReadOnly(false);
                }
            } else {
                setWorkflowReadOnly(false);
            }

            // 3. Apply beforeRenderValueList
            if (beforeRenderValueList) {
                finalOptions = beforeRenderValueList(finalOptions, list, fieldId);
            }

            // 4. Apply customFilterOptions
            if (customFilterOptions) {
                finalOptions = customFilterOptions(finalOptions, list, idFieldTable);
            }

            // 5. Apply environment filter
            if (environmentField) {
                const envFieldValue = getValueId(environmentField?.value);
                const normalizedEnvValue = getPrimaryEnvironmentValue(envFieldValue);

                finalOptions = finalOptions.filter((option) => {
                    const envValues = parseEnvironmentValues(option?.stridenvironment);
                    return (
                        !option?.stridenvironment ||
                        envValues?.length === 0 ||
                        envValues?.includes('-1') ||
                        envValues?.includes(normalizedEnvValue)
                    );
                });
            }

            // 6. Track empty state for disableIfNoOptions (uses finalOptions, not emptyFilter)
            setIsEmptyForDisable(!finalOptions?.length);
            setOptionsLoaded(true);

            // 7. Apply grouping last (groupFunction may expect options, parentValue, mainGroupLabel, otherGroupLabel e.g. getGroupByParentValue)
            return groupFunction
                ? groupFunction(finalOptions, parentValue, mainGroupLabel, otherGroupLabel)
                : finalOptions;
        });
    }, [
        beforeRenderValueList,
        customFilterOptions,
        dependenceFilter,
        dispatch,
        emptyFilter,
        environmentField,
        fieldId,
        groupFunction,
        mainGroupLabel,
        otherGroupLabel,
        hideIfEmptyList,
        idFieldTable,
        list,
        onHideChange,
        parentField,
        parentValue,
        readOnlyProp,
        valueId,
        workFlow,
        workFlowFilter,
        workFlowFirstValue,
    ]);

    // Load options on mount when workflow exists or hideIfEmptyList is enabled
    useEffect(() => {
        if (list && (workFlow || hideIfEmptyList)) {
            loadOptions();
        }
    }, [workFlow, hideIfEmptyList, list, loadOptions]);

    // Preload options when we have a value and getExtraClassNameValue, so SingleValue can apply
    // the correct class on first render (hoi-poi Select only loads options on focus otherwise)
    useEffect(() => {
        if (!shouldUsePreloadedOptions) {
            setPreloadedOptionsForClass([]);
            return;
        }
        loadOptions()
            .then((loaded) => setPreloadedOptionsForClass(loaded || []))
            .catch(() => setPreloadedOptionsForClass([]));
    }, [loadOptions, shouldUsePreloadedOptions]);

    const readOnlyByDependency = readOnlyIfNoParent && isBackendEmpty(parentValue);
    const workflowPendingReadOnly = !!workFlow && !optionsLoaded;
    const readOnly =
        readOnlyProp || readOnlyByDependency || workflowReadOnly || workflowPendingReadOnly;

    const isClearable = workFlow ? false : isClearableProp;
    const disabled = disabledProp || (disableIfNoOptions && isEmptyForDisable);

    // Determine if value needs resolution by SelectHoi
    const isPrimitiveValue = useMemo(() => {
        if (normalizedValue === null || normalizedValue === undefined) return false;
        if (isMulti && Array.isArray(normalizedValue)) {
            return normalizedValue.some((v) => typeof v !== 'object');
        }
        return typeof normalizedValue !== 'object';
    }, [normalizedValue, isMulti]);

    const usePlainValue = usePlainValueProp ?? isPrimitiveValue;

    // Custom option renderer with extra class
    const customOption = useMemo(() => {
        if (customOptionProp) return customOptionProp;
        if (!getExtraClassNameValue) return undefined;
        return (option) => (
            <OneRow label={option.label} className={getExtraClassNameValue(option)} />
        );
    }, [customOptionProp, getExtraClassNameValue]);

    // Resolve labelPopover config:
    // - false → no popover
    // - true → use registry config for list
    // - object → merge registry defaults with overrides
    // - undefined → auto: use registry config if list is known (works in EntityCrud without adding labelPopover)
    const resolvedPopoverConfig = useMemo(() => {
        if (labelPopover === false) return null;

        const registryConfig = getLabelPopoverConfigForList(list);

        if (labelPopover === true) return registryConfig;

        if (typeof labelPopover === 'object') {
            return {
                ...(registryConfig || {}),
                ...labelPopover,
                type: labelPopover.type ?? registryConfig?.type ?? list,
            };
        }

        // Auto: enable popover when list is in registry (no need to pass labelPopover in inputAttrs)
        return registryConfig || null;
    }, [labelPopover, list]);

    // Component override for SingleValue/MultiValueLabel styling and optional LabelPopover
    const componentOverride = useMemo(() => {
        const needsCustomSingleValue = getExtraClassNameValue || resolvedPopoverConfig;
        const needsCustomMultiValueLabel = isMulti && resolvedPopoverConfig;
        if (!needsCustomSingleValue && !needsCustomMultiValueLabel) return props.componentOverride;
        return {
            ...props.componentOverride,
            ...(needsCustomSingleValue && {
                SingleValue: createSingleValueComponent(
                    getExtraClassNameValue,
                    resolvedPopoverConfig,
                ),
            }),
            ...(needsCustomMultiValueLabel && {
                MultiValueLabel: createMultiValueLabelComponent(resolvedPopoverConfig),
            }),
        };
    }, [getExtraClassNameValue, resolvedPopoverConfig, isMulti, props.componentOverride]);

    // When fuzzy (loadOptionsProp), wrap so API results are grouped; list path already applies groupFunction in loadOptions
    const effectiveLoadOptions = useMemo(() => {
        if (!loadOptionsProp || !groupFunction) {
            return loadOptionsProp || (list ? loadOptions : undefined);
        }
        return (text) =>
            Promise.resolve(loadOptionsProp(text)).then((opts) => {
                return groupFunction(opts, parentValue, mainGroupLabel, otherGroupLabel);
            });
    }, [
        loadOptionsProp,
        list,
        loadOptions,
        groupFunction,
        parentValue,
        mainGroupLabel,
        otherGroupLabel,
    ]);

    // Early returns
    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId, list, parentField)) return null;
    if (hideIfEmptyList && isEmptyForHide) return null;

    // Key to force reload when dependencies change
    const envId = getValueId(environmentField?.value);
    const parentId = getValueId(parentValue);
    const selectKey = `${list}-${envId || ''}-${parentId || ''}`;

    return (
        <div className={classnames('fm-field-server-list', className)}>
            <SelectHoi
                key={selectKey}
                label={label}
                onChange={onChange}
                value={normalizedValue}
                options={
                    shouldUsePreloadedOptions && preloadedOptionsForClass.length > 0
                        ? preloadedOptionsForClass
                        : options
                }
                loadOptions={effectiveLoadOptions}
                error={error}
                placeholder={hint}
                placeholderReadOnly={placeholderReadOnly || hint}
                hint={description}
                isRequired={mandatory}
                isClearable={isClearable}
                isMulti={isMulti}
                isFullWidth
                showMediaInSelectedValues
                list={list}
                isFuzzy={isFuzzy}
                usePlainValue={usePlainValue}
                customOption={customOption}
                componentOverride={componentOverride}
                {...props}
                withoutFilter={isFuzzy ? (withoutFilter ?? noFilter ?? true) : withoutFilter}
                isReadOnly={readOnly || disabled}
            />
        </div>
    );
};

Select.displayName = 'Select';
Select.propTypes = propTypes;

export default memo(Select);
